import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  className?: string;
  cta?: { label: string; to: string } | { label: string; onClick: () => void };
  children?: ReactNode;
}

/** Centered empty placeholder with optional primary CTA. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  className,
  cta,
  children,
}: EmptyStateProps): JSX.Element {
  return (
    <div className={cn('flex flex-col items-center justify-center px-4 py-12 text-center', className)}>
      <div className="mb-3 flex size-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-100">
        <Icon className="size-5" aria-hidden />
      </div>
      <p className="text-[13px] font-semibold text-ink-800">{title}</p>
      {description ? (
        <p className="mt-1 max-w-[280px] text-[12px] leading-relaxed text-ink-500">{description}</p>
      ) : null}
      {cta && 'to' in cta ? (
        <Button className="mt-4" size="sm" asChild>
          <Link to={cta.to}>{cta.label}</Link>
        </Button>
      ) : null}
      {cta && 'onClick' in cta ? (
        <Button className="mt-4" size="sm" type="button" onClick={cta.onClick}>
          {cta.label}
        </Button>
      ) : null}
      {children}
    </div>
  );
}
